import { atom, onConnect } from '@reatom/framework'

import { Array, AsyncStateN, pipe, Result } from '@blade/std-lib'
import { delugeIdAtom } from '@root/entities/auth'
import { selectedNetworkAtom } from '@root/entities/network'
import { TrackingWallet } from '@root/entities/wallet-tracking'
import { dataTableNoAccessError, reatomDataTable } from '@root/shared/ui-kit'

import { trackingWalletsTableColumns } from '../ui/tracking-wallets-table/tracking-wallets-table-columns'

import { walletTrackingAddedEvent } from './reatom-add-wallet-tracker'
import { walletTrackingDeletedEvent } from './reatom-delete-wallet-tracking'
import { walletTrackingUpdatedEvent } from './reatom-edit-tracking-wallet-form'
import { getUserTrackingWalletsRequestAction } from './wallet-tracker-requests'

import type { TableData } from '@root/shared/ui-kit'

export type TrackingWalletsTableColumnId =
  | 'name'
  | 'address'
  | 'tags'
  | 'created-at'
  | 'actions'

export const reatomTrackingWalletsTable = () => {
  const paramsAtom = atom((ctx) => {
    return {
      network: ctx.spy(selectedNetworkAtom),
    }
  }, 'paramsAtom')

  const model = reatomDataTable({
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'tracking-wallets-table',
    columns: trackingWalletsTableColumns,
    paramsAtom,
    getId: ({ walletAddress }) => walletAddress,
    loadItems: async (ctx, { filters, params: { network } }) => {
      const delugeId = ctx.get(delugeIdAtom)
      if (!delugeId) {
        return Result.fail(dataTableNoAccessError())
      }
      const result = await getUserTrackingWalletsRequestAction(ctx, {
        delugeId,
        network,
      })

      return pipe(
        result,
        Result.map(
          (it): TableData<TrackingWallet.TrackingWallet> => ({
            data: pipe(it, Array.sort(TrackingWallet.cmpCreatedAtDesc), (x) =>
              x.filter((it) => {
                const tagsFilter = filters.tags
                if (tagsFilter && tagsFilter.type === 'custom') {
                  return tagsFilter.predicate(it.tags)
                }
                return true
              }),
            ),
          }),
        ),
      )
    },
  })

  onConnect(model, () => {
    const unsubscribeDeleted = walletTrackingDeletedEvent.onCall(
      (ctx, { walletAddress: _ }) => {
        model.load(ctx)
      },
    )
    const unsubscribeAdded = walletTrackingAddedEvent.onCall((ctx) => {
      model.load(ctx)
    })

    const unsubscribeUpdated = walletTrackingUpdatedEvent.onCall(
      (ctx, { updatedTrackingWallet }) => {
        const state = ctx.get(model)
        if (!AsyncStateN.isSuccess(state)) {
          return
        }
        const updatedItems = state.value.data.map((item) => {
          if (item.walletAddress === updatedTrackingWallet.walletAddress) {
            return updatedTrackingWallet
          }
          return item
        })
        model.setState(
          ctx,
          AsyncStateN.success({ ...state.value, data: updatedItems }),
        )
      },
    )
    return () => {
      unsubscribeDeleted()
      unsubscribeAdded()
      unsubscribeUpdated()
    }
  })

  return model
}
